import React, { Fragment, useRef } from 'react';
import { RouteComponentProps, Link } from 'react-router-dom';
import withSSR, { SSRProps } from 'utils/withSSR';
import withI18n, { I18nProps } from 'utils/i18n/withI18n';
import Loading from 'components/Loading';
import ErrorBox from 'components/ErrorBox';
import Question from './Question';
import store from '../../store';
import './style.scss';

const HelpIndex: React.FC<SSRProps<{ data }> & I18nProps & RouteComponentProps> = ({
    i18n: { __ },
    data,
    history,
    match,
    currentUser,
    __error__,
    __loading__,
    __getData__
}) => {
    const { url: parentUrl } = match;
    const inputRef = useRef(null);

    function render() {
        const goSearch = () => {
            const value = (inputRef.current as any).value;

            if (value) {
                history.push(`/help/search?keyword=${encodeURIComponent(value)}`);
            }
        };

        const handleInput = e => {
            if (e.nativeEvent.keyCode === 13) {
                goSearch();
            }
        };

        if (__loading__) {
            return <Loading tip="loading..." />;
        }

        if (__error__) {
            return <ErrorBox error={__error__} onClick={__getData__} />;
        }

        if (!data) {
            return null;
        }

        return (
            <Fragment>
                <div
                    className="help-index-banner"
                    style={{
                        backgroundImage: data.help_banner?.url ? `url(${data.help_banner.url})` : ''
                    }}>
                    <h1>{__('Welcome to the Help Center')}</h1>
                    <h2>{__('A faster and easier query experience')}</h2>
                </div>
                <div className="site-main help-index-content">
                    <div className="content">
                        <h2 className="title">{__('Search with key words')}</h2>
                        <div className="search-box">
                            <input
                                className="search"
                                placeholder={__('Search')}
                                ref={inputRef}
                                onKeyPress={handleInput}
                            />
                            <div className="search-icon" onClick={goSearch}></div>
                        </div>
                    </div>
                    <div className="help-index-nav">
                        <h2>{__('Search by types')}</h2>
                        <div className="nav-container">
                            {data.items?.map(item => {
                                const showItem = !!item?.children?.length;

                                return (
                                    <Fragment key={item.id}>
                                        {showItem && (
                                            <div className="nav-group">
                                                {item.icon && item.icon !== 'None' && (
                                                    <img
                                                        className="nav-icon"
                                                        src={item.icon}
                                                        alt={item.title}
                                                        title={item.title}
                                                    />
                                                )}
                                                <div className="nav-group-list">
                                                    <div className="title">
                                                        <h3 className="common-link">
                                                            <Link
                                                                to={`${parentUrl}/${item.link_code}`}
                                                                title={item.title}>
                                                                {item.title}
                                                            </Link>
                                                        </h3>
                                                    </div>
                                                    <div className="sub-nav-group">
                                                        {item.children?.slice(0, 4).map(subItem => {
                                                            return (
                                                                <div className="group-item" key={subItem.id}>
                                                                    <div className="common-link common-link-small">
                                                                        <Link
                                                                            to={`${parentUrl}/${item.link_code}/${subItem.id}`}
                                                                            title={subItem.title}>
                                                                            {subItem.title}
                                                                        </Link>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                        {item.children?.length > 4 && (
                                                            <div className="group-item">
                                                                <div className="common-link common-link-small more">
                                                                    <Link to={`${parentUrl}/${item.link_code}`}>
                                                                        {__('View more')}
                                                                    </Link>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </div>
                    </div>
                    <Question currentUser={currentUser} />
                </div>
            </Fragment>
        );
    }

    return <div className="help-index-root">{render()}</div>;
};

export default withSSR(withI18n(HelpIndex), ({ i18n, currentUser }) => {
    return store.getHelpList(i18n?.language, currentUser).then(data => {
        return { data };
    });
});
